import type { EmblaCarouselType } from "embla-carousel";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * Configuration options for the useEmblaPeek hook.
 */
type UseEmblaPeekOptions = {
  /** Embla carousel API instance */
  emblaApi?: EmblaCarouselType;
  /** Key to store peek state in localStorage */
  fireOnceKey?: string;
  /** Prefix for localStorage keys */
  storagePrefix?: string;
  /** Distance to scroll in pixels */
  peekPx?: number;
  /** Delay before starting the peek animation (ms) */
  delayMs?: number;
  /** Duration to stay in the peeked position (ms) */
  pauseMs?: number;
};

/**
 * Return value of the useEmblaPeek hook.
 */
type UseEmblaPeekResult = {
  /** Function to trigger the peek animation */
  triggerPeek: () => void;
  /** Whether the carousel is currently in a peeking state */
  isPeeking: boolean;
};

const EPSILON_PX = 0.5;

const isAlmost = (value: number, target: number, epsilon = EPSILON_PX) =>
  Math.abs(value - target) <= epsilon;

/**
 * Hook that adds a "peek" animation to an Embla carousel.
 *
 * The peek animation automatically scrolls the carousel a short distance to reveal
 * the next item when the carousel is at the start position, then returns to the
 * start after a pause. This effect fires only once per unique key (if specified)
 * and is cancelled if the user interacts with the carousel.
 *
 * @param options - Configuration options for the peek animation
 * @returns An object containing the `triggerPeek` function and `isPeeking` state
 *
 * @example
 * const { triggerPeek, isPeeking } = useEmblaPeek({
 *   emblaApi: carouselApi,
 *   fireOnceKey: "product-carousel",
 *   peekPx: 80,
 *   delayMs: 500,
 *   pauseMs: 2000
 * });
 */
export function useEmblaPeek(options: UseEmblaPeekOptions): UseEmblaPeekResult {
  const {
    emblaApi,
    fireOnceKey,
    storagePrefix = "embla-peeked:",
    peekPx = 100,
    delayMs = 800,
    pauseMs = 1500
  } = options;

  const storageKey = useMemo(
    () => (fireOnceKey ? `${storagePrefix}${fireOnceKey}` : undefined),
    [fireOnceKey, storagePrefix]
  );

  const storage = typeof window !== "undefined" ? window.localStorage : null;

  const delayTimeoutRef = useRef<number | null>(null);
  const pauseTimeoutRef = useRef<number | null>(null);

  const [isPeeking, setIsPeeking] = useState(false);
  const [hasPeeked, setHasPeeked] = useState<boolean>(() => {
    if (!storageKey) return false;
    return storage?.getItem(storageKey) === "true";
  });

  const clearTimers = useCallback(() => {
    if (delayTimeoutRef.current !== null) {
      window.clearTimeout(delayTimeoutRef.current);
      delayTimeoutRef.current = null;
    }
    if (pauseTimeoutRef.current !== null) {
      window.clearTimeout(pauseTimeoutRef.current);
      pauseTimeoutRef.current = null;
    }
  }, []);

  const cancel = useCallback(() => {
    clearTimers();
    setIsPeeking(false);
    emblaApi?.off("pointerDown", cancel);
  }, [clearTimers, emblaApi]);

  const markFired = useCallback(() => {
    setHasPeeked(true);
    if (storageKey) storage?.setItem(storageKey, "true");
  }, [storage, storageKey]);

  const triggerPeek = useCallback(() => {
    if (!emblaApi) return;
    if (hasPeeked) return;
    if (peekPx <= 0) return;
    if (!emblaApi.canScrollNext()) return;

    const engine = emblaApi.internalEngine?.();
    if (!engine) return;

    // Only peek from the "start" position
    const atStart = isAlmost(engine.offsetLocation.get(), 0);
    if (!atStart) return;

    emblaApi.on("pointerDown", cancel);

    delayTimeoutRef.current = window.setTimeout(() => {
      // scroll negative because Embla's "distance" is reversed w.r.t viewport movement
      engine.scrollTo.distance(-peekPx, false);
      markFired();
      setIsPeeking(true);

      pauseTimeoutRef.current = window.setTimeout(() => {
        if (isAlmost(engine.offsetLocation.get(), -peekPx)) {
          engine.scrollTo.distance(peekPx, false);
        }

        emblaApi?.off("pointerDown", cancel);
        clearTimers();
        setIsPeeking(false);
      }, pauseMs);
    }, delayMs);
  }, [
    emblaApi,
    cancel,
    clearTimers,
    delayMs,
    markFired,
    pauseMs,
    peekPx,
    hasPeeked
  ]);

  // Clean up timers and listeners on unmount / embla change
  useEffect(() => {
    return () => {
      clearTimers();
      emblaApi?.off("pointerDown", cancel);
    };
  }, [clearTimers, emblaApi, cancel]);

  return { triggerPeek, isPeeking };
}
